import { Group, Stack, Text, ThemeIcon } from '@mantine/core';

import { IconEye } from '@tabler/icons-react';

import type { PanelContextValue } from '../../../shared/components/UI/Sidepanel/SidepanelInternalData.provider';

import type { DocumentChunk } from '../../../shared/entities/DocumentChunk';
import type { SourceSidepanelContext } from './SourceSidePanel';

export function SourcesSection({
  sources,
  openPanel,
  panelContext,
}: {
  sources: DocumentChunk[];
  openPanel: (selectedSource: DocumentChunk) => void;
  panelContext: PanelContextValue<SourceSidepanelContext> | undefined;
}) {
  const handleClick = (selectedSource: DocumentChunk) => {
    if (!panelContext) {
      openPanel(selectedSource);
    }

    panelContext?.updateData({ selectedSource });
  };

  return (
    <Stack spacing="02">
      <Text variant="xs" fw={500} c="gray.4">
        Sources
      </Text>
      <Group spacing="02">
        {sources.map((source, index) => (
          <SourceButton
            key={source.metadata.filePath + index}
            onClick={() => handleClick(source)}
            label={formatSourceLabel(source)}
            isActive={
              source.pageContent ===
              panelContext?.data.selectedSource?.pageContent
            }
          />
        ))}
      </Group>
    </Stack>
  );
}

function SourceButton({
  label,
  onClick,
  isActive,
}: {
  label: string;
  onClick: () => void;
  isActive: boolean;
}) {
  return (
    <Group
      onClick={onClick}
      sx={theme => ({
        flexWrap: 'nowrap',
        maxWidth: '264px',
        gap: theme.spacing['02'],
        padding: `${theme.spacing['01']} ${theme.spacing['02']}`,
        borderRadius: theme.radius.md,
        border: `1px solid`,
        borderColor: isActive ? theme.colors.primary[7] : theme.colors.gray[1],
        cursor: 'pointer',
        ':hover': { backgroundColor: theme.colors.gray[0] },
      })}
    >
      <Text variant="sm" fw={500} c="gray.6" truncate>
        {label}
      </Text>
      <ThemeIcon
        size={16}
        color="gray.6"
        variant="outline"
        sx={{ border: 'none' }}
      >
        <IconEye />
      </ThemeIcon>
    </Group>
  );
}

export function formatSourceLabel(source: DocumentChunk) {
  return source.metadata.pageNumber
    ? `${source.metadata.filePath} - Page ${source.metadata.pageNumber}`
    : source.metadata.filePath;
}
